import { useState, type FormEvent } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import {
  ArrowLeft, Eye, EyeOff, Lock, Mail, ShieldCheck, Timer, AlertTriangle,
} from "lucide-react"
import { useAuth } from "../../context/auth-context"
import { ApiError } from "../../lib/api"
import { SCHOOL, SECURITY } from "../../lib/constants"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Alert } from "../../components/ui/Alert"
import { ThemeToggle } from "../../components/ui/ThemeToggle"

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

export default function PortalLogin() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Surface errors that come back from the Google OAuth redirect
  const googleError = searchParams.get("error")
  const googleErrorMap: Record<string, string> = {
    google_auth_cancelled: "Google sign-in was cancelled.",
    google_no_code: "Google did not return an authorisation code. Please try again.",
    google_not_configured: "Google sign-in is not configured on this server.",
    google_token_exchange_failed: "Google authentication failed. Please try again.",
    google_userinfo_failed: "Could not retrieve your Google profile. Please try again.",
    google_no_email: "Your Google account does not have an email address.",
    account_blocked: "Your account has been blocked. Please contact the administrator.",
    account_suspended: "Your account has been suspended. Please contact the administrator.",
  }
  const googleErrorMessage = googleError ? (googleErrorMap[googleError] || "Google sign-in failed. Please try again.") : null

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!email || !password) {
      setError("Please enter your email and password.")
      return
    }
    setLoading(true)
    try {
      await login(email, password)
      toast.success("Welcome back! Login successful.")
      navigate("/portal/dashboard")
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError("Login failed. Please check your connection and try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  function handleGoogle() {
    window.location.href = "/api/auth/google?returnTo=/portal/dashboard"
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-ink-50 dark:bg-ink-950">
      {/* Branding panel (left on desktop) */}
      <aside className="relative md:w-1/2 lg:w-3/5 bg-gradient-to-br from-brand-800 via-brand-900 to-brand-950 text-white overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-30" aria-hidden="true" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-gold-500/10 blur-3xl" aria-hidden="true" />
        <div className="relative h-full flex flex-col p-8 md:p-12 lg:p-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt={`${SCHOOL.shortName} logo`} className="w-12 h-12 rounded-lg bg-white/95 p-1.5" />
            <div>
              <div className="font-display font-bold text-lg leading-tight">{SCHOOL.shortName}</div>
              <div className="text-xs text-white/70">Student Portal</div>
            </div>
          </div>

          <div className="mt-auto max-w-md">
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Welcome to your <span className="text-gradient-gold">Student Portal</span>
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-6">
              Access your courses, results, payments, ID card, transcripts and more — all in one secure place.
            </p>
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> Bank-grade security with session monitoring</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> 24/7 access from any device</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold-300" /> Real-time results & transcript verification</li>
            </ul>
          </div>

          <div className="mt-10 text-xs text-white/60">
            © {new Date().getFullYear()} {SCHOOL.fullName}. All rights reserved.
          </div>
        </div>
      </aside>

      {/* Form panel (right on desktop) */}
      <main className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-600 dark:text-ink-300 hover:text-brand-700 dark:hover:text-brand-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to website
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="md:hidden flex items-center gap-3 mb-6 justify-center">
              <img src="/logo.png" alt={`${SCHOOL.shortName} logo`} className="w-10 h-10 rounded-lg" />
              <div className="font-display font-bold text-ink-900 dark:text-ink-100">{SCHOOL.shortName}</div>
            </div>

            <div className="mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 dark:text-ink-100 mb-1">
                Login to Portal
              </h2>
              <p className="text-sm text-ink-500 dark:text-ink-400">
                Enter your credentials to access your student dashboard.
              </p>
            </div>

            {(error || googleErrorMessage) && (
              <Alert variant="danger" className="mb-4 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{error || googleErrorMessage}</span>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="pl-9 bg-white dark:bg-ink-900"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-brand-700 dark:text-brand-400 hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="pl-9 pr-9 bg-white dark:bg-ink-900"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600 dark:hover:text-ink-200"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />
                    Signing in...
                  </>
                ) : "Login to Portal"}
              </Button>
            </form>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 h-px bg-ink-200 dark:bg-ink-800" />
              <span className="text-xs uppercase tracking-wider text-ink-400">or</span>
              <div className="flex-1 h-px bg-ink-200 dark:bg-ink-800" />
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full bg-white dark:bg-ink-900"
              onClick={handleGoogle}
              disabled={loading}
            >
              <GoogleIcon />
              Continue with Google
            </Button>

            <p className="mt-6 text-center text-sm text-ink-600 dark:text-ink-400">
              Don't have an account?{" "}
              <Link to="/portal/register" className="font-semibold text-brand-700 dark:text-brand-400 hover:underline">
                Create one
              </Link>
            </p>

            {/* Security note */}
            <div className="mt-6 rounded-lg border border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/60 p-4">
              <div className="flex items-center gap-2 mb-2 text-ink-800 dark:text-ink-200">
                <ShieldCheck className="w-4 h-4 text-brand-700 dark:text-brand-400" />
                <span className="text-xs font-semibold uppercase tracking-wider">Security Notice</span>
              </div>
              <ul className="space-y-1.5 text-xs text-ink-600 dark:text-ink-400">
                <li className="flex items-start gap-2">
                  <Timer className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-600 dark:text-gold-400" />
                  Sessions expire after <strong className="mx-1">{SECURITY.SESSION_TIMEOUT_MINUTES} minutes</strong> of inactivity.
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-600 dark:text-gold-400" />
                  Account locks for <strong className="mx-1">{SECURITY.LOGIN_LOCKOUT_MINUTES} minutes</strong> after <strong className="mx-1">{SECURITY.MAX_LOGIN_ATTEMPTS} failed attempts</strong>.
                </li>
                <li className="flex items-start gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gold-600 dark:text-gold-400" />
                  All login attempts are logged with IP and device information.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
